import React from "react";

import { datadogRum } from "@datadog/browser-rum";
import { useWorkOSAuth } from "@worktango/d2c-service/workOSReact";

import { Loader } from "@kazoohr/confetti";
import { coalesce, doNothing } from "@kazoohr/helpers";
import { AsyncRoute as _AsyncRoute } from "@kazoohr/router";

import { useGetCurrentUserQuery } from "../generated/apiHooks";

import { ArchivedAccountPage } from "./components/auth/ArchivedAccountPage";

export function ConstellationRoutes() {
  const { state, user } = useWorkOSAuth();

  React.useEffect(() => {
    if (user) {
      const name = coalesce(
        [user.firstName, user.lastName].filter(Boolean).join(" ").trim(),
        user.email
      );

      datadogRum.setUser({
        id: user.id,
        email: user.email,
        name,
      });
      datadogRum.startSessionReplayRecording();

      const pendoApiKey = window.__APP_DATA__?.pendoConfig?.apiKey;
      if (pendoApiKey && window.pendo) {
        window.pendo.initialize({
          visitor: {
            id: user.id,
            email: user.email,
            fullName: name,
          },
        });
      }
    }
  }, [user]);

  // Routes accessible without authentication
  const publicRoutes = (
    <React.Fragment key="public">
      <AsyncRoute
        exact
        path={[
          "/constellation/:id/generate-ideas",
          "/constellation/:id/rank-ideas",
          "/constellation/:id/rank-implementations",
        ]}
        module={() => import("./components/sections/QuestionSection")}
        component={(module) => module["QuestionSection"]}
      />
      <AsyncRoute
        exact
        path="/constellation/:id/discussion"
        module={() => import("./components/sections/DiscussionSection")}
        component={(module) => module["DiscussionSection"]}
      />
      <AsyncRoute
        exact
        path="/constellation/:id/generate-implementations"
        module={() => import("./components/sections/SectionFourView")}
        component={(module) => module["SectionFourView"]}
      />
      <AsyncRoute
        exact
        path="/constellation/:id/join"
        module={() => import("./components/join/JoinSession")}
        component={(module) => module["JoinSession"]}
      />
      <AsyncRoute
        exact
        path="/constellation/:id/waiting"
        module={() => import("./components/join/Waiting")}
        component={(module) => module["Waiting"]}
      />
      <AsyncRoute
        exact
        path="/constellation/:id/feedback"
        module={() => import("./components/closing/ClosingFeedback")}
        component={(module) => module["ClosingFeedback"]}
      />
      <AsyncRoute
        exact
        path="/constellation/:id/session-feedback"
        module={() => import("./components/feedback/FeedbackView")}
        component={(module) => module["FeedbackView"]}
      />
      <AsyncRoute
        exact
        path="/constellation/:id/insights"
        module={() => import("./components/insights/SessionInsights")}
        component={(module) => module["SessionInsights"]}
      />
      <AsyncRoute
        exact
        path="/constellation/:id/participant/:participantId"
        module={() => import("./components/participant/ParticipantDetailsPage")}
        component={(module) => module["ParticipantDetailsPage"]}
      />
    </React.Fragment>
  );

  return state !== "authenticated" ? (
    <React.Fragment key="unauthenticated">
      {publicRoutes}
      <AsyncRoute
        exact
        path="/"
        module={() => import("./components/auth/SignInPage")}
        component={(module) => module["SignInPage"]}
      />
    </React.Fragment>
  ) : (
    <React.Fragment key="authenticated">
      {publicRoutes}
      <AuthenticatedRoutes />
    </React.Fragment>
  );
}

function AuthenticatedRoutes() {
  const { data, loading, error } = useGetCurrentUserQuery({
    onError: doNothing, // Error is handled by the provider
  });

  if (loading) {
    return <Loader centered />;
  }

  if (
    error?.message === "User account is archived" ||
    data?.currentUser?.status === "ARCHIVED"
  ) {
    return <ArchivedAccountPage />;
  }

  return (
    <>
      <AsyncRoute
        exact
        path="/"
        module={() => import("./components/welcome/Welcome")}
        component={(module) => module["Welcome"]}
      />
      <AsyncRoute
        exact
        path="/constellation/new"
        module={() => import("./components/creation/CreateSession")}
        component={(module) => module["CreateSession"]}
      />
      <AsyncRoute
        exact
        path="/constellation/edit/:id"
        module={() => import("./components/creation/CreateSession")}
        component={(module) => module["CreateSession"]}
      />
      <AsyncRoute
        exact
        path="/constellation/new/:id"
        module={() => import("./components/creation/CreateSessionSuccess")}
        component={(module) => module["CreateSessionSuccess"]}
      />
      <AsyncRoute
        exact
        path="/constellation/test/attributes"
        module={() => import("./components/creation/Attributes")}
        component={(module) => module["Attributes"]}
      />
      <AsyncRoute
        exact
        path="/settings/attributes"
        module={() => import("./components/settings/AttributesSettings")}
        component={(module) => module["AttributesSettings"]}
      />
      <AsyncRoute
        exact
        path="/velocity/:id"
        module={() => import("./components/velocity/VelocityDebugPage")}
        component={(module) => module["VelocityDebugPage"]}
      />
      <AsyncRoute
        exact
        path="/debug"
        module={() => import("./components/debug/DebugIndex")}
        component={(module) => module["DebugIndex"]}
      />
      <AsyncRoute
        exact
        path="/debug/constellation/:id/question-page"
        module={() => import("./components/debug/DebugQuestionPage")}
        component={(module) => module["DebugQuestionPage"]}
      />
      <AsyncRoute
        exact
        path="/debug/constellation/:id/ranking-page"
        module={() => import("./components/debug/DebugRankingPage")}
        component={(module) => module["DebugRankingPage"]}
      />
      <AsyncRoute
        exact
        path="/debug/constellation/:id/discussion-page"
        module={() => import("./components/debug/DebugDiscussionPage")}
        component={(module) => module["DebugSection3Page"]}
      />
      <AsyncRoute
        exact
        path="/admin/organizations"
        module={() => import("./components/admin/OrganizationsPage")}
        component={(module) => module["OrganizationsPage"]}
      />
      <AsyncRoute
        exact
        path={["/admin/users", "/admin"]}
        module={() => import("./components/admin/UsersTable")}
        component={(module) => module["UsersTable"]}
      />
      <AsyncRoute
        exact
        path="/organization"
        module={() => import("./components/organization/OrganizationPage")}
        component={(module) => module["OrganizationPage"]}
      />
    </>
  );
}

function AsyncRoute(props: React.ComponentProps<typeof _AsyncRoute>) {
  return <_AsyncRoute fallback={<Loader centered />} {...props} />;
}
